import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import {
  Package, Layers, AlertTriangle, TrendingDown, CalendarClock, XCircle, Bell, ClipboardList, Send,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useNavigate } from "react-router-dom";

const COLORS = ["#042C53", "#0a8f8f", "#F59E0B", "#10B981", "#6366F1", "#EF4444"];

const Stat = ({ icon: Icon, label, value, color, testid, onClick }) => (
  <Card
    className={`p-5 bg-white border-slate-200 ${onClick ? "cursor-pointer hover:border-slate-300 transition-colors duration-200" : ""}`}
    data-testid={testid}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm text-slate-500 font-medium">{label}</div>
        <div className="font-serif text-4xl tracking-tight mt-1 text-navy">{value}</div>
      </div>
      <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
        <Icon size={22} style={{ color }} strokeWidth={2} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard").then((r) => r.data),
  });

  if (isLoading || !data) return <div className="text-slate-400" data-testid="dashboard-loading">Loading overview…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-serif text-3xl tracking-tight text-navy">Welcome, {user.name.split(" ")[0]}</h2>
        <p className="text-slate-500 text-sm mt-1">Here is the current state of your inventory and compliance alerts.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Package} label="Item Types" value={data.total_items} color="#042C53" testid="stat-items" />
        <Stat icon={Layers} label="Active Batches" value={data.total_batches} color="#0a8f8f" testid="stat-batches" />
        <Stat icon={TrendingDown} label="Low Stock" value={data.low_stock} color="#F59E0B" testid="stat-low" />
        <Stat icon={XCircle} label="Out of Stock" value={data.out_of_stock} color="#EF4444" testid="stat-out" />
        <Stat icon={CalendarClock} label="Expiring ≤30d" value={data.expiring_soon} color="#F59E0B" testid="stat-expiring" />
        <Stat icon={AlertTriangle} label="Expired In Stock" value={data.expired} color="#EF4444" testid="stat-expired" />
        <Stat icon={Bell} label="Active Alerts" value={data.active_alerts} color="#0a8f8f" testid="stat-alerts" />
        <Stat icon={ClipboardList} label="Consumption Logs" value={data.consumption_entries} color="#042C53" testid="stat-consumption" />
        <Stat icon={Send} label="Pending Requisitions" value={data.pending_requisitions} color="#6366F1" testid="stat-requisitions" onClick={() => nav("/requisitions")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 bg-white border-slate-200 lg:col-span-1" data-testid="category-chart">
          <h3 className="font-serif text-xl text-navy tracking-tight mb-4">Stock by Category</h3>
          {data.category_breakdown.length === 0 ? (
            <div className="text-slate-400 text-sm">No stock yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.category_breakdown} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {data.category_breakdown.map((e, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-6 bg-white border-slate-200 lg:col-span-2" data-testid="recent-alerts">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl text-navy tracking-tight">Priority Alerts</h3>
            <button onClick={() => nav("/alerts")} className="text-sm font-medium text-teal hover:underline" data-testid="view-all-alerts">
              View all
            </button>
          </div>
          {data.recent_alerts.length === 0 ? (
            <div className="text-slate-400 text-sm">No active alerts. All stock healthy. ✓</div>
          ) : (
            <div className="space-y-2">
              {data.recent_alerts.map((a, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{ borderColor: a.severity === "danger" ? "#EF444455" : "#F59E0B55", background: a.severity === "danger" ? "#FEF2F2" : "#FFFBEB" }}
                  data-testid={`alert-row-${i}`}
                >
                  <AlertTriangle size={18} style={{ color: a.severity === "danger" ? "#EF4444" : "#F59E0B" }} className="mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-700">{a.message}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
